/**
 * Shared validation rules for the user profile fields (name, email,
 * organization) used by the public signup form and the admin "Add User" modal.
 *
 * Each rule is defined once here so the two forms can never drift apart.
 * Password rules live in `passwordValidation.ts` and email format in
 * `validators.ts`; between them these three files hold every field rule.
 *
 * Every helper returns an error message, or an empty string when the value is
 * valid. Deciding *when* to show the message (touched state, submit) stays with
 * the form.
 */
import { isValidEmail } from './validators';

/** Validate a first/last name: required, non-blank once trimmed, max 255 chars. */
export const getNameFieldError = (value: string | undefined, label: string): string => {
  if (!value) return `${label} is required`;
  if (!value.trim()) return `${label} cannot be blank`;
  if (value.trim().length > 255) return `${label} is too long (max 255 characters)`;
  return '';
};

/** Validate an email address: required and correctly formatted. */
export const getEmailFieldError = (value: string | undefined): string => {
  if (!value) return 'Email is required';
  if (!isValidEmail(value)) return 'Please enter a valid email address';
  return '';
};

/** Validate an optional organization name: max 255 chars when provided. */
export const getOrgNameFieldError = (value: string | undefined): string => {
  if ((value?.trim() ?? '').length > 255) return 'Organization name is too long (max 255 characters)';
  return '';
};
